import React from 'react';
import { motion } from 'framer-motion';
interface CircleVisualProps {
  radius?: number;
  diameter?: number;
  circumference?: number;
  area?: number;
  label?: string;
  showFormula?: boolean;
  highlightRadius?: boolean;
  highlightDiameter?: boolean;
  mode?: 'stimulus' | 'support';
}
export function CircleVisual({
  radius: propRadius,
  diameter: propDiameter,
  circumference,
  area,
  label,
  showFormula = false,
  highlightRadius = false,
  highlightDiameter = false,
  mode = 'support'
}: CircleVisualProps) {
  // Calculate missing values
  const radius = propRadius ?? (propDiameter ? propDiameter / 2 : 50);
  const diameter = propDiameter ?? radius * 2;
  // SVG dimensions
  const svgSize = 280;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // Scale circle to fill SVG nicely — ensure minimum visible size
  // Map real-world radius to display radius: fill ~80% of SVG half-width
  const maxDisplayRadius = svgSize * 0.38; // ~106px
  const minDisplayRadius = 40;
  const circleRadius = Math.max(Math.min(maxDisplayRadius, maxDisplayRadius), minDisplayRadius);
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-5542">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-5543">
          {label}
        </p>}

      {/* Circle SVG */}
      <motion.svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="drop-shadow-md" data-id="element-5544">
        {/* Circle */}
        <motion.circle cx={cx} cy={cy} r={circleRadius} fill="rgb(191, 219, 254)" stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1,
        ease: 'easeInOut'
      }} data-id="element-5545" />

        {/* Center point */}
        <circle cx={cx} cy={cy} r="4" fill="rgb(59, 130, 246)" data-id="element-5546" />

        {/* Radius line (if highlighted) */}
        {highlightRadius && <>
            <motion.line x1={cx} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(239, 68, 68)" strokeWidth="3" strokeDasharray="5,5" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-5547" />
            <text x={cx + circleRadius / 2} y={cy - 10} textAnchor="middle" className="text-sm font-bold fill-red-600" data-id="element-5548">
              r = {radius}
            </text>
          </>}

        {/* Diameter line (if highlighted) */}
        {highlightDiameter && <>
            <motion.line x1={cx - circleRadius} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(34, 197, 94)" strokeWidth="3" strokeDasharray="5,5" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-5549" />
            <text x={cx} y={cy + 25} textAnchor="middle" className="text-sm font-bold fill-green-600" data-id="element-5550">
              d = {diameter}
            </text>
          </>}
      </motion.svg>

      {/* Formula display */}
      {mode === 'support' && showFormula && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-2 max-w-md" data-id="element-5551">
          {circumference !== undefined && <div className="flex items-center justify-between gap-4" data-id="element-5552">
              <span className="text-sm font-semibold text-blue-900" data-id="element-5553">
                Omtrek (C):
              </span>
              <span className="text-base font-bold text-blue-700" data-id="element-5554">
                C = 2πr = 2 × π × {radius} ≈ {circumference.toFixed(1)} cm
              </span>
            </div>}
          {area !== undefined && <div className="flex items-center justify-between gap-4" data-id="element-5555">
              <span className="text-sm font-semibold text-blue-900" data-id="element-5556">
                Oppervlakte (A):
              </span>
              <span className="text-base font-bold text-blue-700" data-id="element-5557">
                A = πr² = π × {radius}² ≈ {area.toFixed(1)} cm²
              </span>
            </div>}
          {!circumference && !area && <div className="space-y-1" data-id="element-5558">
              <p className="text-sm text-blue-900" data-id="element-5559">
                <span className="font-bold" data-id="element-5560">Omtrek:</span> C = 2πr of C = πd
              </p>
              <p className="text-sm text-blue-900" data-id="element-5561">
                <span className="font-bold" data-id="element-5562">Oppervlakte:</span> A = πr²
              </p>
            </div>}
        </div>}

      {/* Measurements card */}
      {mode === 'support' && (propRadius || propDiameter) && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-5563">
          <div className="flex flex-col gap-1" data-id="element-5564">
            {propRadius && <div className="flex items-center gap-2" data-id="element-5565">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-5566">
                  Straal (r):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-5567">
                  {radius} cm
                </span>
              </div>}
            {propDiameter && <div className="flex items-center gap-2" data-id="element-5568">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-5569">
                  Diameter (d):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-5570">
                  {diameter} cm
                </span>
              </div>}
          </div>
        </div>}
    </div>;
}